import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminMenu from "../../AdminMenu";

import {
  GoogleMap,
  useLoadScript,
  Marker,
  Polygon,
  Autocomplete,
  OverlayView,
} from "@react-google-maps/api";

import { Button } from "primereact/button";
import { GoogleMapsApiKey } from "../../../constants/FormateDate";
import { getUserData } from "../../../utils/localStorage";
import {
  addBrandStorePolygon,
  checkStorePolygonCongestion,
  deleteStorePolygon,
  searchStorePolygon,
  updateBrandStorePolygon,
} from "../../../services/StoreUsers/PostStoreUsers";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import SuccessModal from "../../SuccessModal";

const DEFAULT_CENTER = { lat: 28.6139, lng: 77.209 };
const LIBRARIES = ["marker", "places"]; // static, prevents reload warning

const GeoLocationViewMap = ({resAPIKey , id , StoreData} ) => {
  const navigate = useNavigate();
  // const location = useLocation();
  // const { id, StoreData } = location.state || {}; // Fallback to prevent undefined errors
 
  const { userProfileRole, brandProfileData } = getUserData();
  const userRole = userProfileRole;
  let brandId = brandProfileData.id;

  const [editPolygonData, setEditPolygonData] = useState([]);
  const [congestionPolygon, setCongestionPolygon] = useState([]);

  const editPolygonInfo = editPolygonData?.[0];

  const autocompleteRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalStatus, setModalStatus] = useState(false);
  const [modalText, setModalText] = useState("");
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const [zoneName, setZoneName] = useState("");

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: resAPIKey,
    libraries: LIBRARIES,
  });

  const [userLocation, setUserLocation] = useState(DEFAULT_CENTER);
  const [points, setPoints] = useState([]); // points while drawing
  const [polygon, setPolygon] = useState(null); // saved polygon
  const [finishPolygonView, setFinishPolygonView] = useState(false);

  const mapRef = useRef(null);

  // Ref to the actual Polygon instance (needed to read its live MVCArray path)
  const polygonRef = useRef(null);
  // Keep references to the path event listeners so we can clean them up properly
  const polygonListenersRef = useRef([]);
  // The path we hand to <Polygon path={...}> for its INITIAL render only.
  // We deliberately do NOT update this from syncPolygonPath — see note below.
  const initialPathRef = useRef(null);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();

    if (!place.geometry) return;

    const location = place.geometry.location;

    const newCenter = {
      lat: location.lat(),
      lng: location.lng(),
    };

    setUserLocation(newCenter);

    // smooth pan
    if (mapRef.current) {
      mapRef.current.panTo(newCenter);
    }
  };

  const fetchStorePolygon = () => {
    const data = {
      storeId: id,
    };
    searchStorePolygon(data)
      .then((response) => {
        if (!response.data.error) {
          const Res = response.data;
          setEditPolygonData(Res);
        }
      })
      .catch((err) => {
        console.error("Error:", err);
      });
  };

  // Get user's current location
  useEffect(() => {
    fetchStorePolygon();

    if (navigator.geolocation) {
      if (StoreData?.address?.latitude) {
        setUserLocation({
          lat: StoreData?.address?.latitude,
          lng: StoreData?.address?.longitude,
        });
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          (error) => {
            console.error("Error getting location:", error);
            setUserLocation(DEFAULT_CENTER);
          },
        );
      }
    }
  }, []);

  useEffect(() => {
    if (editPolygonData?.length > 0) {
      const removeLastPoint = editPolygonInfo?.coordinates.pop();
      const newMapPolygon = editPolygonInfo?.coordinates.map((point) => ({
        lat: point?.[1],
        lng: point?.[0],
      }));
      setPoints(newMapPolygon);
      initialPathRef.current = [...newMapPolygon];
      setPolygon([...newMapPolygon]);
      setFinishPolygonView(true);
    }
  }, [editPolygonData, editPolygonInfo]);

  // Handle clicks to add points
  const handleMapClick = (e) => {
    setPoints((prev) => [
      ...prev,
      { lat: e.latLng.lat(), lng: e.latLng.lng() },
    ]);
  };

  // Finish polygon
  const finishPolygon = () => {
    if (points.length < 3) {
      alert("Polygon needs at least 3 points!");
      return;
    }

    initialPathRef.current = [...points];
    setPolygon([...points]);
    setPoints([]);
    setFinishPolygonView(true);

    // Optionally fit map to polygon bounds
    if (mapRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend(p));
      mapRef.current.fitBounds(bounds);
    }
  };

  const handleDeleteStorePolygon = () => {
    deleteStorePolygon(id)
      .then((response) => {
        if (!response.data.error) {
          const Res = response.data;
          fetchStorePolygon();
        }
      })
      .catch((err) => {
        console.error("Error:", err);
      });
  }

  // Remove polygon
  const removePolygon = () => {
    initialPathRef.current = null;
    setPolygon(null);
    handleDeleteStorePolygon()
  };

  // Undo last point
  const undoPoint = () => {
    setPoints((prev) => prev.slice(0, -1));
  };

  // Clear map reference when unmounted
  const onUnmount = () => {
    mapRef.current = null;
  };

  // Read the polygon's live path (MVCArray) and push it back into React state
  // (used for the points-preview panel and for the save/update payload).
  // This runs whenever a vertex is moved, a new vertex is inserted (by dragging
  // an edge midpoint), or a vertex is removed (right-click on a vertex).
  //
  // IMPORTANT: `polygon` state updated here must NOT be fed back into the
  // <Polygon path={...}> prop. If it were, every edit would give that prop a
  // new array reference, the @react-google-maps/api wrapper would call
  // polygon.setPath(newArray) internally, which replaces the shape's MVCArray
  // entirely — silently detaching the set_at/insert_at/remove_at listeners
  // below (they were attached to the OLD array). That's why only the first
  // dragged point used to get captured: after that first sync, the path prop
  // changed, the array got swapped, and nothing was listening anymore.
  // Instead, <Polygon> is given a frozen `initialPathRef` that never changes
  // after creation, so the same MVCArray (and its listeners) stays alive for
  // the whole editing session, and every subsequent drag keeps firing.
  const syncPolygonPath = () => {
    if (!polygonRef.current) return;
    const path = polygonRef.current.getPath();
    const nextPoints = path.getArray().map((latLng) => ({
      lat: latLng.lat(),
      lng: latLng.lng(),
    }));
    setPolygon(nextPoints);
  };

  // Called once when the <Polygon> mounts / whenever it's (re)created.
  // Attaches listeners directly to the polygon's MVCArray path, since
  // onDragEnd on <Polygon> only fires for whole-shape drags, not for
  // individual vertex drags or edge-midpoint-drag-to-insert.
  const onPolygonLoad = (poly) => {
    polygonRef.current = poly;
    const path = poly.getPath();

    // Clean up any previously attached listeners first (defensive, in case
    // onLoad ever fires again on the same instance)
    polygonListenersRef.current.forEach((listener) => listener.remove());

    polygonListenersRef.current = [
      window.google.maps.event.addListener(path, "set_at", syncPolygonPath), // existing vertex dragged
      window.google.maps.event.addListener(path, "insert_at", syncPolygonPath), // new vertex created via edge drag
      window.google.maps.event.addListener(path, "remove_at", syncPolygonPath), // vertex removed
    ];
  };

  // Clean up listeners when the polygon unmounts (e.g. removePolygon() sets polygon to null)
  const onPolygonUnmount = () => {
    polygonListenersRef.current.forEach((listener) => listener.remove());
    polygonListenersRef.current = [];
    polygonRef.current = null;
  };

  // Whole-shape drag (dragging the polygon body, not a single vertex)
  const handleDragEnd = () => {
    syncPolygonPath();
  };

  if (!isLoaded) return <div>Loading map...</div>;

  const handleSubmit = () => {
    // Logic for creating a new pickup request
    if (!isButtonPressed) {
      // Set the state to indicate that the button has been pressed
      setIsButtonPressed(true);

      const data = {
        coordinates: polygon.map((point) => [point.lng, point.lat]),
        brandId: brandId,
        storeId: id,
        name: zoneName,
      };

      const updateData = {
        id: editPolygonInfo?.id,
        coordinates: polygon.map((point) => [point.lng, point.lat]),
        brandId: brandId,
        storeId: id,
      };

      // Call the service function for creating pickup request

      const checkData = {
        id: editPolygonInfo?.id,
        coordinates: polygon.map((point) => [point.lng, point.lat]),
        brandId: brandId,
        storeId: id,
      };

      checkStorePolygonCongestion(checkData)
        .then((response) => {
          if (!response.data.error) {
            const Res = response.data;
            if (Res.length === 0) {
              setCongestionPolygon([]);
              setIsButtonPressed(false);
              // sendResponse({message, status, modalFun: false});
              // Reset state variables
              if (editPolygonData?.length > 0) {
                updateBrandStorePolygon(updateData)
                  .then((response) => {
                    if (!response.data.error) {
                      const Res = response.data;
                      if (Res) {
                        const message = Res.message;
                        const status = Res.status;
                        // sendResponse({message, status, modalFun: false});
                        // Reset state variables
                        if (status === true) {
                          setModalVisible(true);
                          setModalStatus("success");
                          setModalText(message);
                          fetchStorePolygon();
                          // setTimeout(() => {
                          //   navigate("/StoreManagementList");
                          // }, 1500);
                          setIsButtonPressed(false);
                        } else {
                          setModalVisible(true);
                          setModalStatus("failed");
                          setModalText(message);
                          setIsButtonPressed(false);
                        }
                      }
                    }
                  })
                  .catch((err) => {
                    console.error("Error:", err);
                  });
              } else {
                addBrandStorePolygon(data)
                  .then((response) => {
                    if (!response.data.error) {
                      const Res = response.data;
                      if (Res) {
                        const message = Res.message;
                        const status = Res.status;
                        // sendResponse({message, status, modalFun: false});
                        // Reset state variables
                        if (status === true) {
                          setModalVisible(true);
                          setModalStatus("success");
                          setModalText(message);
                          fetchStorePolygon();
                          // setTimeout(() => {
                          //   navigate("/StoreManagementList");
                          // }, 1500);
                          setIsButtonPressed(false);
                        } else {
                          setModalVisible(true);
                          setModalStatus("failed");
                          setModalText(message);
                          setIsButtonPressed(false);
                        }
                      }
                    }
                  })
                  .catch((err) => {
                    console.error("Error:", err);
                  });
              }
            } else {
              setIsButtonPressed(false);
              // const newMapPolygon = Res[0]?.coordinates?.map((point) => ({
              //   lat: point?.[1],
              //   lng: point?.[0],
              // }));
              setCongestionPolygon(Res);
            }
          }
        })
        .catch((err) => {
          console.error("Error:", err);
        });
    }
  };

  return (
    <div className="flex flex-column h-full">
      {/* Header */}
      <div
        className="h-3rem surface-0 sticky z-2 top-0 flex justify-content-center align-items-center"
        style={{ "--surface-0": "#1752AA" }}
      >
        <div className="flex-1 flex ml-3 mr-3">
          <div className="flex-1 flex">
            <div className="ml-3 flex justify-content-center align-items-center">
              <div className="h-1rem w-1rem border-circle flex justify-content-center align-items-center">
                <i
                  className="pi pi-angle-left"
                  onClick={() => navigate("/StoreManagementList")}
                  style={{ fontSize: "27px", cursor: "pointer", color: "#fff" }}
                ></i>
              </div>
            </div>
            <div className="flex-1 ml-5">
              <p className="text-2xl text-white font-bold uppercase">
                Geo Location
              </p>
            </div>
          </div>
          <div
            className="flex justify-content-center align-items-center cursor-pointer"
            onClick={() => setVisible(true)}
          >
            <i
              className="pi pi-bars"
              style={{ fontSize: "27px", marginRight: 30, color: "#fff" }}
            ></i>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      {StoreData?.address?.latitude && StoreData?.address?.longitude ? 
      <div className="flex flex-1 p-4 gap-4">
        {/* MAP SECTION */}
        {/* 🗺️ MAP CARD */}
        <div
          className="flex-1 border-round-xl shadow-3 overflow-hidden"
          style={{
            background: "#fff",
            position: "relative",
          }}
        >
          <div style={{ position: "absolute", top: 10, left: 10, zIndex: 10 }}>
            <Autocomplete
              onLoad={(ref) => (autocompleteRef.current = ref)}
              onPlaceChanged={onPlaceChanged}
            >
              <input
                type="text"
                placeholder="Search location..."
                style={{
                  width: "260px",
                  height: "40px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  outline: "none",
                }}
              />
            </Autocomplete>
          </div>

          <GoogleMap
            zoom={15}
            center={userLocation}
            mapContainerStyle={{ height: "450px", width: "100%" }}
            onClick={handleMapClick}
            onLoad={(map) => (mapRef.current = map)}
            onUnmount={onUnmount}
          >
            <Marker
              position={userLocation}
              icon={{
                url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
              }}
            />

            {points.map((p, i) => (
              <Marker key={i} position={p} />
            ))}

            {polygon && polygon.length > 2 && (
              <Polygon
                // Deliberately NOT `path={polygon}` — see the note above
                // syncPolygonPath for why that breaks multi-point edits.
                path={initialPathRef.current}
                onLoad={onPolygonLoad}
                onUnmount={onPolygonUnmount}
                onDragEnd={handleDragEnd}
                options={{
                  draggable: true, // Allows the user to click and drag the whole shape
                  editable: true, // Allows dragging individual vertices AND dragging an
                  // edge midpoint out to create a brand new vertex there
                  fillColor: "#2196F3",
                  fillOpacity: 0.25,
                  strokeColor: "#0D47A1",
                  strokeWeight: 2,
                }}
              />
            )}

            {congestionPolygon.map((cp, i) => (
              <React.Fragment key={cp?.id}>
              <Polygon
                // Deliberately NOT `path={polygon}` — see the note above
                // syncPolygonPath for why that breaks multi-point edits.
                path={cp?.coordinates?.map((point) => ({
                  lat: point?.[1],
                  lng: point?.[0],
                }))}
                options={{
                  // draggable: true, // Allows the user to click and drag the whole shape
                  // editable: true, // Allows dragging individual vertices AND dragging an
                  // edge midpoint out to create a brand new vertex there
                  fillColor: "#f32121",
                  fillOpacity: 0.25,
                  strokeColor: "#a10d0d",
                  strokeWeight: 2,
                }}
              />

              {/* Render the Text Label at the Center */}
            <OverlayView
              position={{ lat: cp?.storeInfo?.address?.latitude, lng: cp?.storeInfo?.address?.longitude }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div style={{
                background: 'white',
                color: '#da13d0',
                padding: '4px 8px',
                borderRadius: '4px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                fontSize: '14px',
                fontWeight: 'bold',
                transform: 'translate(-50%, -50%)', // Centers the text block perfectly over the coordinate
                whiteSpace: 'nowrap'
              }}>
                {cp?.storeInfo?.storeName + " | " + cp?.storeInfo?.storeCode}
              </div>
            </OverlayView>
            </React.Fragment>
            ))}
          </GoogleMap>
        </div>

        {/* CONTROL PANEL */}
        <div
          className="surface-card border-round shadow-3 p-4 flex flex-column gap-4"
          style={{ width: "320px" }}
        >
          {editPolygonData.length === 0 && (
            <>
              <h3 className="m-0 text-primary">Zone Controls</h3>
              <FloatLabel>
                <InputText
                  id="zoneName"
                  value={zoneName}
                  onChange={(e) => setZoneName(e.target.value)}
                  className="w-full"
                />
                <label htmlFor="zoneName">Zone Name</label>
              </FloatLabel>
            </>
          )}

          {/* Buttons */}
          <div className="flex flex-column gap-2">
            {points.length > 2 && (
              <Button
                label="Finish Polygon"
                icon="pi pi-check"
                onClick={finishPolygon}
                className="p-button-success"
              />
            )}

            {points.length >= 1 && (
              <Button
                label="Undo Last Point"
                icon="pi pi-undo"
                onClick={undoPoint}
                className="p-button-warning"
              />
            )}

            {polygon?.length > 0 && (
              <Button
                label="Remove Polygon"
                icon="pi pi-trash"
                onClick={removePolygon}
                className="p-button-danger"
              />
            )}
            {polygon?.length > 0 && finishPolygonView && (
              <Button
                label={
                  editPolygonData?.length > 0
                    ? "Update Polygon"
                    : "Save Polygon"
                }
                icon="pi pi-save"
                onClick={handleSubmit}
                className="p-button-primary"
                disabled={isButtonPressed}
              />
            )}
          </div>

          {/* Polygon Points Preview */}
          {polygon?.length > 0 && (
            <div
              className="p-3 border-round surface-100"
              style={{ maxHeight: "200px", overflowY: "auto" }}
            >
              <h4 className="mt-0 mb-2">Points</h4>
              {polygon.map((p, i) => (
                <div key={i} className="text-sm mb-1">
                  #{i + 1}: {p.lat.toFixed(4)}, {p.lng.toFixed(4)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      : 
      
       <div className="flex justify-content-center align-items-center mt-4">
                        <p className="text-3xl font-semi-bold">Please Enter Store Location</p>
                    </div>
      
      }
      <SuccessModal
        setModalVisible={setModalVisible}
        modalVisible={modalVisible}
        status={modalStatus}
        text={modalText}
      />

      <AdminMenu setVisible={setVisible} visible={visible} />
    </div>
  );
};

export default GeoLocationViewMap;
